import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IUseCountDownParameters {
	initTimeSeconds: number;
	start: boolean;
	startTime: number;
	callFinish?: (...arguments_: any) => void | Promise<void>;
	callLast10Sec?: (...arguments_: any) => void;
	callPer200Ms?: (...arguments_: any) => void;
	callPer50Ms?: (...arguments_: any) => void;
	callPer5Sec?: (...arguments_: any) => void;
	callPerSec?: (...arguments_: any) => void;
}

const useCountDown = ({
	initTimeSeconds,
	startTime,
	start,
	callPer200Ms,
	callPer50Ms,
	callPerSec,
	callPer5Sec,
	callLast10Sec,
	callFinish,
}: IUseCountDownParameters) => {
	const timeReference = useRef(initTimeSeconds);
	const [displayTime, setDisplayTime] = useState('');
	const { t } = useTranslation('translation', { keyPrefix: 'countdown' });
	const frameLoopReference = useRef<() => void>();
	const rafRef = useRef<number | null>(null);
	const gameOverReference = useRef(false);

	const updateTime = (seconds: number) => {
		if (seconds <= 0) {
			setDisplayTime('00:00');

			return;
		}

		timeReference.current = seconds;

		const days = Math.floor(timeReference.current / 86_400);
		const hours = Math.floor((timeReference.current % 86_400) / 3600);
		const minutes = Math.floor((timeReference.current % 3600) / 60);
		const secs = timeReference.current % 60;

		const pad = (n: number) => String(n).padStart(2, '0');
		let formatted = '';

		if (days > 0) {
			formatted = `${days} ${t('day')}. ${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
		} else if (hours > 0) {
			formatted = `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
		} else {
			formatted = `${pad(minutes)}:${pad(secs)}`;
		}

		setDisplayTime(formatted);
	};

	const startGame = () => {
		const duration = initTimeSeconds * 1000;
		const start = startTime;
		let lastReported200MS: number | null = null;
		let lastReported50MS: number | null = null;
		let lastReportedSec: number | null = null;
		let lastReported5Sec: number | null = null;
		let warningTriggered = false;

		gameOverReference.current = false;

		function frameLoop() {
			const now = Date.now();
			const elapsed = now - start;
			const remaining = Math.max(0, duration - elapsed);
			const secondsLeft = Math.ceil(remaining / 1000);

			if (secondsLeft <= 0) {
				updateTime(secondsLeft);
			}

			const current50MS = Math.floor(elapsed / 50);

			if (current50MS !== lastReported50MS) {
				lastReported50MS = current50MS;
				callPer50Ms?.();
			}

			const current200MS = Math.floor(elapsed / 200);

			if (current200MS !== lastReported200MS) {
				lastReported200MS = current200MS;
				callPer200Ms?.();
			}

			// Каждые 1 сек
			const currentSec = Math.floor(elapsed / 1000);

			if (currentSec !== lastReportedSec) {
				lastReportedSec = currentSec;
				updateTime(secondsLeft);
				callPerSec?.(secondsLeft);
			}

			// Каждые 5 сек
			const current5sec = Math.floor(elapsed / 5000);

			if (current5sec !== lastReported5Sec) {
				lastReported5Sec = current5sec;
				callPer5Sec?.(); // твоя функция
			}

			// 10 сек до конца — смена UI
			if (!warningTriggered && remaining <= 10_000) {
				warningTriggered = true;
				callLast10Sec?.(); // твоя функция
			}

			// Конец игры
			if (remaining <= 0 && !gameOverReference.current) {
				gameOverReference.current = true;
				frameLoopReference.current = undefined;
				callFinish?.(); // твоя функция

				return;
			}

			if (!gameOverReference.current && timeReference.current >= 0) {
				rafRef.current = requestAnimationFrame(frameLoop);
			}
		}

		frameLoopReference.current = frameLoop;
		rafRef.current = requestAnimationFrame(frameLoop);
	};

	useEffect(() => {
		if (rafRef.current && timeReference.current < 0) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
	}, [timeReference.current]);

	useEffect(() => {
		console.log('use countdown call', { initTimeSeconds, start, startTime });
		setDisplayTime('');
		gameOverReference.current = true;
		timeReference.current = 0;

		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}

		if (!start || startTime === 0) return;

		timeReference.current = initTimeSeconds;
		startGame();
	}, [start, startTime]);

	useEffect(() => {
		// const handleVisibilityChange = () => {
		// 	if (document.visibilityState === 'visible' && frameLoopReference.current && !gameOverReference.current) {
		// 		gameOverReference.current = false;
		// 		requestAnimationFrame(frameLoopReference.current);
		// 	}
		// };

		// document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			// document.removeEventListener('visibilitychange', handleVisibilityChange);
			gameOverReference.current = true;
		};
	}, []);

	return { displayTime, timeInSec: timeReference.current };
};

export default useCountDown;
